import { type FieldType } from '@databases/model/field';
import { ImportedFileType } from '@databases/types/database';
import { type ParseResult, type ParseMeta } from 'papaparse';
import { type IntlShape } from 'react-intl-next';

export type ParsedData = Record<string, unknown> & { __parsed_extra?: unknown[] };

export interface ExtendedParseResult<T> extends ParseResult<T> {
  data: Array<T & { __parsed_extra?: unknown[] }>;
  meta: ParseMeta & { renamedHeaders: Record<string, string> };
}

export type CSVSupportedFieldTypes =
  | FieldType.Checkbox
  | FieldType.Date
  | FieldType.Hyperlink
  | FieldType.Number
  | FieldType.Select
  | FieldType.Text;

export type FieldTypeAdditionalProperties = {
  [FieldType.Select]: {
    uniqueTags: string[];
  };
  [FieldType.Hyperlink]: {
    hyperlinkValues: string[][];
  };
  [FieldType.Date]: {
    dateValues: Date[];
  };
  // No additional properties for Number, Text, Checkbox
  [FieldType.Number]: undefined;
  [FieldType.Text]: undefined;
  [FieldType.Checkbox]: undefined;
};

type FieldTypeMappingValue = {
  type: CSVSupportedFieldTypes;
  additionalProperties?: FieldTypeAdditionalProperties[CSVSupportedFieldTypes];
};

export type FieldTypeMapping = Map<string, FieldTypeMappingValue>;

export type ImportOption = 'append' | 'replace';

export type ImportConfig = {
  option?: ImportOption;
  simulateDelay?: boolean;
  convertDate?: boolean;
};

export type ParsedImportFile = {
  name: string;
  mimeType: string;
  rawContent: string;
  size: number;
  type: ImportedFileType.CSV;
  content: string;
};

// Don't auto sort, step order matters
export enum ImportSteps {
  fileReceived,
  csvParsed,
  conversionCompleted,
  importCompleted,
}

export interface FieldNameConversionProps {
  fieldNames: readonly string[];
  index: number;
  type?: FieldType;
  currentNames: string[];
  intl: IntlShape;
}

/**
 * The supported import MIME types.
 */
export const supportedImportMimeTypes: Record<string, ImportedFileType> = {
  'text/csv': ImportedFileType.CSV,
  'text/plain': ImportedFileType.TEXT,
  'text/tab-separated-values': ImportedFileType.CSV,
  'application/vnd.ms-excel': ImportedFileType.CSV, // CSV file type for Windows
};
